import {
  ImageScaleView,
  ImageView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import { Match2Timer } from '../../components/Match2Timer';
import TeamChestProgressBar from './TeamChestProgressBar';
import StateObserver, { now, user } from 'src/StateObserver';
import {
  getCurrentTeamEventId,
  getTeamEventScore,
} from 'src/replicant/team/team.getters';
import {
  getTeamChestEventRemainingTime,
  getTeamChestEventStatus,
  getTeamChestEventTargetScore,
} from 'src/replicant/team/chest/teamChest.getters';
import { assertNever } from 'src/replicant/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import { TeamChestChest } from './TeamChestChest';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import buttons from 'src/lib/ui/config/buttons';
import { Feature } from 'src/replicant/analytics';
import { withLoading } from 'src/lib/utils';
import { TeamEventData } from 'src/replicant/team/competition/teamCompetition.ruleset';
import {
  getCurrentTeamCompetition,
  getCurrentTeamProfile,
} from '../team.sequence';

export class TeamChestHeader extends View {
  private bg: ImageScaleView;
  private timer: Match2Timer;
  private progressBar: TeamChestProgressBar;
  private title: ImageView;
  private teamInfo: View;
  private teamName: LangBitmapFontTextView;
  private teamText: LangBitmapFontTextView;
  private teamAvatar: Avatar;
  private chest: TeamChestChest;
  private pet1: ImageScaleView;
  private pet2: ImageScaleView;
  private congratulations: View;
  private congratulationsRibbon: ImageScaleView;
  private congratulationsTitle: LangBitmapFontTextView;
  private congratulationsSubtitle: LangBitmapFontTextView;
  private openButton: ButtonScaleViewWithText;
  public onOpenChest?: () => void;

  constructor(opts: ViewOpts) {
    super({ ...opts, width: 590, height: 438 });

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      image: 'assets/ui/match2/team/chest/team_chest_header_bg.png',
    });

    this.timer = new Match2Timer({
      superview: this,
      x: this.style.width * 0.5,
      y: this.style.height,
      centerOnOrigin: true,
    });
    this.timer.setRemainingTimeGetter(getTeamChestEventRemainingTime);

    this.progressBar = new TeamChestProgressBar({
      superview: this,
      x: 80,
      y: 280,
    });
    this.progressBar.setProgress({
      current: 0,
      total: 1,
    });

    this.title = new ImageView({
      superview: this,
      width: 450,
      height: 109,
      image: `assets/ui/match2/team/chest/team_chest_title_${StateObserver.getLocale()}.png`,
      centerOnOrigin: true,
      x: this.style.width * 0.5,
      y: -10,
    });

    this.teamInfo = new View({
      superview: this,
      x: 40,
      y: 130,
    });

    this.teamAvatar = new Avatar({
      superview: this.teamInfo,
      width: 100,
      height: 100,
      centerOnOrigin: false,
    });

    this.teamName = new LangBitmapFontTextView({
      superview: this.teamInfo,
      x: this.teamAvatar.style.x + this.teamAvatar.style.width + 10,
      y: this.teamAvatar.style.y + 5,
      align: 'left',
      size: 40,
      font: 'Title',
      width: 300,
      height: 40,
      wordWrap: true,
      localeText: () => '',
    });

    this.teamText = new LangBitmapFontTextView({
      superview: this.teamInfo,
      x: this.teamName.style.x,
      y: this.teamName.style.y + this.teamName.style.height,
      align: 'left',
      size: 20,
      font: 'Body',
      color: '#591613',
      width: 400,
      height: 100,
      wordWrap: true,
    });

    this.congratulations = new View({
      superview: this,
      x: this.style.width * 0.5,
      y: 140,
    });

    this.congratulationsRibbon = new ImageScaleView({
      superview: this.congratulations,
      centerOnOrigin: true,
      image: 'assets/ui/match2/team/chest/team_chest_ribbon.png',
      width: 500,
      height: 64,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: {
          left: 40,
          right: 40,
        },
      },
    });

    this.congratulationsTitle = new LangBitmapFontTextView({
      superview: this.congratulations,
      align: 'center',
      verticalAlign: 'center',
      y: -3,
      centerOnOrigin: true,
      size: 30,
      font: 'Title',
      width: this.congratulationsRibbon.style.width - 60,
      height: this.congratulationsRibbon.style.height,
      wordWrap: true,
      localeText: () => i18n('match2.team.chest.complete_title'),
    });

    this.congratulationsSubtitle = new LangBitmapFontTextView({
      superview: this.congratulations,
      x: this.congratulationsTitle.style.x,
      y: this.congratulationsTitle.style.y + 60,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 30,
      font: 'Title',
      width: this.congratulationsTitle.style.width,
      height: this.congratulationsTitle.style.height,
      wordWrap: true,
      localeText: () => i18n('match2.team.chest.complete_subtitle'),
    });

    this.chest = new TeamChestChest({
      superview: this,
      x: 510,
      y: 270,
    });

    this.pet1 = new ImageScaleView({
      superview: this,
      width: 146,
      height: 172,
      scaleMethod: 'contain',
      image: 'assets/ui/match2/team/chest/team_chest_pet1_happy.png',
      centerOnOrigin: true,
      x: 50,
      y: 300,
    });

    this.pet2 = new ImageScaleView({
      superview: this,
      width: 135,
      height: 150,
      scaleMethod: 'contain',
      image: 'assets/ui/match2/team/chest/team_chest_pet2_happy.png',
      centerOnOrigin: true,
      x: this.chest.style.x + 15,
      y: this.chest.style.y - 75,
    });

    this.openButton = new ButtonScaleViewWithText({
      superview: this,
      ...buttons.secondary,
      fontSize: 40,
      labelOffsetY: -2,
      font: 'Body',
      width: 230,
      height: 90,
      x: this.style.width * 0.5 - 115,
      y: 260,
      localeText: () => i18n('match2.team.chest.open'),
      onClick: async () => this.onOpenChest?.(),
      tracking: {
        feature: Feature.MATCH2.TEAM.CHEST._,
        $subFeature: Feature.MATCH2.TEAM.CHEST.OPEN_BUTTON,
      },
    });

    this.showAsLoading();
  }

  public async update(data?: TeamEventData) {
    // Clear previous state while loading.
    this.showAsLoading();

    const competitionPromise =
      Promise.resolve(data) ?? getCurrentTeamCompetition()?.get();
    const teamProfile = getCurrentTeamProfile()?.get();
    const [competition, profile] = await withLoading(
      () => Promise.all([competitionPromise, teamProfile]),
      {
        superview: this,
        feature: Feature.TEAM._,
        subFeature: Feature.TEAM.CHEST_HEADER_UPDATE,
      },
    );
    if (!competition || competition.type !== 'chest') return;

    const teamScore = competition.score;
    const targetScore = competition.targetScore;
    const rewardStatus = competition.status;

    this.progressBar.setProgress({
      current: teamScore,
      total: targetScore,
    });

    this.teamName.localeText = () => profile?.name ?? '';
    this.teamAvatar.updateImage(profile?.icon ?? '');

    switch (rewardStatus) {
      case 'in-progress':
        this.showAsInProgress(targetScore);
        break;
      case 'complete-no-contribution':
        this.showAsCompleteNoContribution();
        break;
      case 'complete-rewards-available':
        this.showAsCompleteRewardsAvailable();
        break;
      case 'complete-already-claimed':
        this.showAsCompleteAlreadyClaimed();
        break;
      default:
        assertNever(rewardStatus);
    }
  }

  private showAsLoading() {
    this.congratulations.hide();
    this.openButton.hide();
    this.progressBar.hide();
    this.teamInfo.hide();
    this.teamText.hide();
    this.chest.hide();
    this.pet1.hide();
    this.pet2.hide();
    this.timer.stopAutoUpdate();
    this.timer.hide();
  }

  private showAsInProgress(targetScore: number) {
    this.congratulations.hide();
    this.openButton.hide();
    this.progressBar.show();
    this.teamInfo.show();
    this.teamText.show();
    this.teamText.localeText = () =>
      i18n('match2.team.chest.in_progress', { num: targetScore });
    this.chest.showClosed();
    this.pet1.hide();
    this.pet2.hide();
    this.timer.show();
    this.timer.autoUpdate();
  }

  private showAsCompleteNoContribution() {
    this.congratulations.hide();
    this.openButton.hide();
    this.progressBar.show();
    this.teamInfo.show();
    this.teamText.localeText = () => i18n('match2.team.chest.no_contribution');
    this.chest.showGreyed();
    this.pet1.hide();
    this.pet2.show();
    this.pet2.updateOpts({
      image: 'assets/ui/match2/team/chest/team_chest_pet2_sad.png',
    });
    this.timer.stopAutoUpdate();
    this.timer.setMessage(i18n('match2.team.chest.finished'));
  }

  private showAsCompleteRewardsAvailable() {
    this.congratulations.show();
    this.congratulationsSubtitle.localeText = () =>
      i18n('match2.team.chest.complete_subtitle');
    this.openButton.show();
    this.progressBar.hide();
    this.teamInfo.hide();
    this.chest.showClosed();
    this.pet1.show();
    this.pet2.show();
    this.pet2.updateOpts({
      image: 'assets/ui/match2/team/chest/team_chest_pet2_happy.png',
    });
    this.timer.stopAutoUpdate();
    this.timer.setMessage(i18n('match2.team.chest.finished'));
  }

  private showAsCompleteAlreadyClaimed() {
    this.congratulations.show();
    this.congratulationsSubtitle.localeText = () =>
      i18n('match2.team.chest.already_claimed');
    this.openButton.hide();
    this.progressBar.hide();
    this.teamInfo.hide();
    this.chest.showOpened();
    this.pet1.hide();
    this.pet2.hide();
    this.timer.stopAutoUpdate();
    this.timer.setMessage(i18n('match2.team.chest.finished'));
  }
}
